'use client'

import { IRedux } from '@/_store/types'
import { IBranch } from '@/_types'
import { BRAND_LOGO } from '@/_utils/Constants'
import { RefObject, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import YandexMapFrame from '@/_components/Map/shared/mapBridge/YandexMapFrame'
import { useMapBridge, buildMapSrc } from '@/_components/Map/shared/mapBridge/useMapBridge'
import { MAP_MODES, MESSAGE_TYPES } from '@/_components/Map/shared/mapBridge/constants'

const DEFAULT_CENTER: [number, number] = [41.31119825, 69.27975936]
const DEFAULT_ZOOM = 11

interface YandexMapProps {
  data: IBranch[]
  /** Ref forwarded to the map bridge's imperative `setCenter` */
  mapRef: RefObject<any>
  onSelect: (branch: IBranch) => void
  activeShipperId?: string
}

export default function YandexMap({ data, mapRef, onSelect }: YandexMapProps) {
  const { active_geometries, data: shipperData } = useSelector(
    (state: IRedux) => state.settings
  )

  const mapCenter = active_geometries ?? DEFAULT_CENTER
  const clusterColor = shipperData?.data?.color ?? 'blue'

  const sendMarkers = (branches: IBranch[] | undefined) => {
    const origin = window.location.origin
    postToFrame(MESSAGE_TYPES.SET_MARKERS, {
      clusterColor,
      markers: (branches ?? [])
        .filter((branch) => typeof branch.location?.lat === 'number' && typeof branch.location?.long === 'number')
        .map((branch) => {
          const logo = BRAND_LOGO[branch.shipper_id]
          return {
            id: branch.id,
            coords: [branch.location.lat, branch.location.long],
            name: branch.name,
            address: branch.address,
            // BRAND_LOGO paths are root-relative to this app, so they have
            // to be made absolute to be loadable from the widget's origin.
            iconType: logo ? 'branded' : undefined,
            iconUrl: logo ? `${origin}${logo}` : undefined,
          }
        }),
    })
  }

  const { iframeRef, postToFrame } = useMapBridge(({ type, data: messageData }) => {
    switch (type) {
      case MESSAGE_TYPES.MAP_READY:
        sendMarkers(data)
        break
      case MESSAGE_TYPES.MARKER_SELECTED: {
        const branch = data?.find((item) => item.id === messageData?.id)
        if (branch) onSelect(branch)
        break
      }
      default:
        break
    }
  })

  // Keep the widget's markers in sync whenever the branch list changes.
  useEffect(() => {
    sendMarkers(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!mapRef) return
    mapRef.current = {
      setCenter: (coords: number[], zoom = DEFAULT_ZOOM) => {
        postToFrame(MESSAGE_TYPES.SET_CENTER, { center: coords, zoom })
      },
    }
  }, [mapRef, postToFrame])

  const iframeSrc = useMemo(
    () =>
      buildMapSrc({
        lat: mapCenter[0],
        lng: mapCenter[1],
        zoom: DEFAULT_ZOOM,
        mode: MAP_MODES.SELF_PICKUP,
      }),
    [mapCenter]
  )

  return (
    <div className='wrapper' style={{ position: 'relative', width: '100%', height: '100%', flex: 1 }}>
      <YandexMapFrame ref={iframeRef} src={iframeSrc} className='mmap' />
    </div>
  )
}
